import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { pipe, tap, switchMap, throwError, map, catchError } from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { HttpParams } from '@angular/common/http';
import { EquipmentAsyncService } from 'src/app/app-services-async/equipment-async.service';
import {
  initHttpParams,
  IWHEquipmentPreviewDTO,
  WHHttpParams,
} from '@workheld/workheld-shared-lib';

type EquipmentState = {
  equipments: IWHEquipmentPreviewDTO;
  pageSize: number;
  isLoading: boolean;
  filters: WHHttpParams;
  customerName: string;
};

const initialState: EquipmentState = {
  equipments: null,
  pageSize: 20,
  isLoading: false,
  filters: { ...new WHHttpParams(), sort: 'equipmentNumber', sortMode: 'ASC' },
  customerName: '',
};

export const EquipmentStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    numTotalEntries: computed(() => store.equipments()?.numTotalEntries),
  })),
  withMethods((store, equipmentService = inject(EquipmentAsyncService)) => ({
    setSearchTerm(searchTerm: string): void {
      patchState(store, (state) => ({
        filters: { ...state.filters, term: searchTerm },
      }));
    },
    setPage(page: number): void {
      patchState(store, (state) => ({
        filters: { ...state.filters, page },
      }));
    },
    setSort(sort: string): void {
      patchState(store, (state) => ({
        filters: { ...state.filters, sort },
      }));
    },
    setIncludeDecommissioned(includeDecommissioned: boolean): void {
      patchState(store, (state) => ({
        filters: { ...state.filters, includeDecommissioned },
      }));
    },
    setCustomerId(customerId: string): void {
      patchState(store, (state) => ({
        filters: { ...state.filters, customerId },
      }));
    },
    setSortMode(sortMode: 'ASC' | 'DESC'): void {
      patchState(store, (state) => ({
        filters: { ...state.filters, sortMode },
      }));
    },
    setCustomerName(customerName: string): void {
      patchState(store, (state) => ({
        customerName,
      }));
    },
    resetStore(): void {
      patchState(store, initialState);
    },
    loadEquipments: rxMethod<any>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() => {
          const httpParams: HttpParams = initHttpParams(store.filters());
          const result = equipmentService.getEquipmentsPreview(httpParams);
          if (!result) {
            patchState(store, { isLoading: false });
            return [];
          }
          return result.pipe(
            map((equipmentPreviewDTO: IWHEquipmentPreviewDTO) =>
              patchState(store, {
                equipments: {
                  ...equipmentPreviewDTO,
                  data: equipmentPreviewDTO.equipments,
                },
                isLoading: false,
              }),
            ),
            catchError((err) => {
              patchState(store, { isLoading: false });
              return throwError(() => err);
            }),
          );
        }),
      ),
    ),
  })),
);
